/**
 * product-addons.js
 *
 * Horizon's <product-form-component> handles the main product's add-to-cart
 * itself (its own fetch, its own events) — it doesn't rely on a plain form
 * `submit` event we can just intercept. So instead of fighting it, we let it
 * do its thing, then listen for the `cart:update` event it dispatches on a
 * successful add, and make a second, separate /cart/add.js call for the
 * selected add-ons right after.
 *
 * Note: the exact event Horizon dispatches has changed across versions
 * (some versions use the `@theme/events` module's CartUpdateEvent, others
 * a plain `cart:update` CustomEvent on document). This listens for the
 * plain event, which covers the common case. If your add-ons still don't
 * fire, check your theme's version against the console for the event name
 * actually being dispatched.
 */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-addons]').forEach((addonBlock) => {
    initAddonBlock(addonBlock);
    initTotalDisplay(addonBlock);
  });
});

function getMainQuantity() {
  const quantityInput = document.querySelector(
    'form[action*="/cart/add"] input[name="quantity"]'
  );
  const value = parseInt(quantityInput?.value || '1', 10);
  return Number.isNaN(value) || value < 1 ? 1 : value;
}

function initTotalDisplay(addonBlock) {
  const totalEl = addonBlock.querySelector('[data-addons-total]');
  if (!totalEl) return;

  let basePriceCents = parseInt(totalEl.dataset.basePrice || '0', 10);
  const variantPrices = parseVariantPrices(totalEl);
  const valueEl = totalEl.querySelector('[data-addons-total-value]');

  const update = () => {
    const mainQuantity = getMainQuantity();
    const addonCents = getSelectedAddons(addonBlock, mainQuantity).reduce(
      (sum, addon) => sum + Math.round(addon.price * addon.quantity * 100),
      0
    );
    valueEl.textContent = formatMoney(basePriceCents * mainQuantity + addonCents);
  };

  addonBlock.addEventListener('change', update);

  // Recalculate live if the customer changes the main product's quantity,
  // since that can affect both the base price total and any synced add-ons.
  const quantityInput = document.querySelector(
    'form[action*="/cart/add"] input[name="quantity"]'
  );
  if (quantityInput) {
    quantityInput.addEventListener('change', update);
    quantityInput.addEventListener('input', update);
  }

  // Keep the base price in sync when the customer switches variants.
  // Horizon (and most modern themes) update the hidden variant `id` input
  // on the product form and dispatch a variant-change event; we look up
  // that new variant's price from the map embedded at page load rather
  // than making an extra request.
  const handleVariantChange = () => {
    const variantInput = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
    );
    if (!variantInput || variantPrices[variantInput.value] === undefined) return;
    basePriceCents = variantPrices[variantInput.value];
    update();
  };

  ['variant:update', 'variant:change'].forEach((eventName) => {
    document.addEventListener(eventName, handleVariantChange);
  });

  update(); // set initial value on load
}

function parseVariantPrices(totalEl) {
  try {
    const ids = JSON.parse(totalEl.dataset.variantIds || '[]');
    const prices = JSON.parse(totalEl.dataset.variantPricesList || '[]');
    const map = {};
    ids.forEach((id, index) => {
      map[id] = prices[index];
    });
    return map;
  } catch {
    return {};
  }
}

function formatMoney(cents) {
  // Use the theme's own formatter (handles the shop's currency/locale
  // formatting correctly) if it's available; otherwise fall back to a
  // plain USD-style format.
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents, window.Shopify.money_format);
  }
  return `$${(cents / 100).toFixed(2)}`;
}

function initAddonBlock(addonBlock) {
  // Capture the main product's quantity at the moment of submit, before
  // Horizon's own handler potentially resets the quantity field back to 1
  // after a successful add. This runs synchronously during the same
  // `submit` event dispatch, ahead of any async reset in a .then() callback.
  const form = document.querySelector('form[action*="/cart/add"]');
  if (form) {
    form.addEventListener('submit', () => {
      addonBlock.dataset.pendingMainQuantity = String(getMainQuantity());
    });
  }

  document.addEventListener('cart:update', async () => {
    const mainQuantity = parseInt(
      addonBlock.dataset.pendingMainQuantity || String(getMainQuantity()),
      10
    );
    const selectedAddons = getSelectedAddons(addonBlock, mainQuantity);
    if (selectedAddons.length === 0) return;

    // Guard against re-adding add-ons if cart:update fires again later
    // (e.g. from an unrelated quantity change in the cart drawer).
    if (addonBlock.dataset.addonsAdded === 'true') return;

    try {
      addonBlock.dataset.addonsAdded = 'true';
      await addAddonsToCart(selectedAddons);
      refreshCartDrawer();
    } catch (err) {
      console.error('[product-addons] Failed to add add-ons:', err);
      addonBlock.dataset.addonsAdded = 'false';
    }
  });
}

async function addAddonsToCart(selectedAddons) {
  const bundleId = crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const items = selectedAddons.map((addon) => ({
    id: addon.variantId,
    quantity: addon.quantity,
    properties: {
      _bundle_id: bundleId,
      _addon_label: addon.label,
    },
  }));

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.description || 'Cart add failed for add-ons');
  }

  return response.json();
}

async function refreshCartDrawer() {
  const cart = await (await fetch('/cart.js')).json();

  try {
    const { CartUpdateEvent } = await import('@theme/events');
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'product-addons', {
        itemCount: cart.item_count,
        source: 'product-addons',
        sections: {},
      })
    );
  } catch {
    // Fallback for theme versions without the @theme/events module.
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount: cart.item_count, source: 'product-addons' } },
      })
    );
  }
}

function getSelectedAddons(addonBlock, mainQuantity = 1) {
  const selected = [];

  addonBlock.querySelectorAll('[data-addon-group]').forEach((group) => {
    const syncQuantity = group.dataset.syncQuantity === 'true';

    group.querySelectorAll('input:checked').forEach((input) => {
      if (!input.value) return; // skip the "None" radio option
      const baseQuantity = parseInt(input.dataset.quantity || '1', 10);
      selected.push({
        variantId: input.dataset.variantId,
        label: input.dataset.label,
        price: parseFloat(input.dataset.price || '0'),
        quantity: syncQuantity ? baseQuantity * mainQuantity : baseQuantity,
      });
    });
  });

  return selected;
}